import { useRouter } from '@application/utils';
import { LoadingContainer } from '@presentation/components';
import { MtLandingBreederPage } from '@presentation/pages';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useCurrentProfileStep } from '../MtBreederOnboardingPageController/api/useCurrentProfileStep';
import { useFormatAnnouncements } from './api/useFormatAnnouncements';
import { useGetAnnouncements } from './api/useGetAnnouncements';
import { useUpdateProfile } from './api/useUpdateProfile';

export const MtLandingBreederPageController: FC = () => {
  const { navigateToRoute } = useRouter();
  const {
    profileValidated,
    currentProfileProgress,
    hasAddedAnimals,
    hasAddedLitters,
    profileLoading,
    onboardingStep,
  } = useCurrentProfileStep();

  const { updateProfileStartForm, updateProfileLoading } = useUpdateProfile();

  // Skip loading when user didn't complete questionnaire
  const {
    announcements,
    announcementsCount,
    announcementsLoading,
    announcementsFetchMoreLoading,
    announcementsFetchMore,
  } = useGetAnnouncements(currentProfileProgress < 2);
  const { formattedAnnouncements } = useFormatAnnouncements(announcements);

  const completeProfileClickHandler = useCallback(() => {
    updateProfileStartForm();
    navigateToRoute('mtOnboarding');
  }, [navigateToRoute, updateProfileStartForm]);

  const onLoadMoreAnnouncementsClickHandler = useCallback(async () => {
    announcementsFetchMore({
      variables: {
        skip: formattedAnnouncements?.length,
      },
    });
  }, [announcementsFetchMore, formattedAnnouncements?.length]);

  const canLoadMoreAnnouncements = useMemo(() => {
    if (!announcementsCount || !formattedAnnouncements) return false;
    return announcementsCount > formattedAnnouncements?.length;
  }, [announcementsCount, formattedAnnouncements]);

  if (profileLoading) {
    return <LoadingContainer />;
  }

  return (
    <MtLandingBreederPage
      loading={updateProfileLoading}
      currentStep={currentProfileProgress}
      currentProgress={currentProfileProgress}
      onboardingStep={onboardingStep}
      profileValidated={profileValidated}
      hasAddedAnimals={hasAddedAnimals}
      hasAddedLitters={hasAddedLitters}
      onCompleteProfileClick={completeProfileClickHandler}
      announcementsLoading={announcementsLoading}
      announcements={formattedAnnouncements}
      onLoadMoreAnnouncementsClick={onLoadMoreAnnouncementsClickHandler}
      canLoadMoreAnnouncements={canLoadMoreAnnouncements}
      loadMoreAnnouncementsLoading={announcementsFetchMoreLoading}
    />
  );
};
